import { useState, type FormEvent } from "react";

const SUBMIT_URL = "https://readdy.ai/api/form/dagle33ep053ijub2cm0";

const INTENTS = ["订阅最新动态", "提交事件线索", "信息纠错反馈", "商务合作"];

export default function ContactSection() {
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [message, setMessage] = useState("");

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    // 蜜罐字段若被自动填充，则静默按成功处理，不向后台提交
    const honeypot = String(data.get("contact_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setMessage("已收到你的订阅，欢迎持续关注沸点追踪的最新动态。");
      form.reset();
      return;
    }

    const payload = new FormData();
    const name = String(data.get("name") || "").trim();
    const email = String(data.get("email") || "").trim();
    const intent = String(data.get("intent") || "").trim();
    const content = String(data.get("content") || "").trim();
    if (name) payload.append("name", name);
    if (email) payload.append("email", email);
    if (intent) payload.append("intent", intent);
    if (content) payload.append("content", content);

    try {
      const res = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(payload as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText) as Record<string, unknown>;
      } catch {
        parsed = null;
      }
      const meta = (parsed?.meta as Record<string, unknown>) || {};
      const serverMsg =
        typeof meta?.message === "string" && meta.message
          ? meta.message
          : typeof parsed?.message === "string"
            ? parsed.message
            : typeof meta?.detail === "string"
              ? meta.detail
              : "";
      const spamHit = /spam|form data is spam/i.test(JSON.stringify(parsed || responseText));
      const ok = res.ok && (parsed?.code === "OK") && !spamHit;

      if (ok) {
        setStatus("success");
        setMessage(serverMsg || "提交成功！我们会尽快处理并回复你。");
        form.reset();
      } else {
        setStatus("error");
        setMessage(serverMsg || "提交失败，请稍后重试或通过邮件联系我们。");
      }
    } catch {
      setStatus("error");
      setMessage("网络异常，提交未成功，请稍后再试。");
    }
  }

  return (
    <section id="subscribe" className="bg-background-100 py-10 md:py-14">
      <div className="container-x">
        <div className="relative overflow-hidden rounded-xl bg-primary-600 p-6 md:p-10">
          <div
            className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_8%_15%,oklch(var(--background-50)/0.16),transparent_40%),radial-gradient(circle_at_95%_90%,oklch(var(--accent-500)/0.35),transparent_45%)]"
            aria-hidden="true"
          ></div>
          <div className="relative grid grid-cols-1 items-center gap-8 lg:grid-cols-2">
            <div className="text-background-50">
              <h2 className="font-heading text-xl font-black leading-snug md:text-3xl">
                新事件开追，第一时间告诉你
              </h2>
              <p className="mt-3 max-w-md text-sm leading-relaxed text-background-100">
                留下邮箱，我们会在重要事件出现突破性进展时推送提醒；也欢迎通过本表单提交你发现的新事件线索或纠错信息。
              </p>
              <ul className="mt-5 space-y-2 text-sm text-background-100">
                <li className="flex items-center gap-2">
                  <i className="ri-check-double-line text-accent-200" aria-hidden="true"></i>
                  突破性进展第一时间提醒
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-double-line text-accent-200" aria-hidden="true"></i>
                  事件脉络与辟谣同步更新
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-double-line text-accent-200" aria-hidden="true"></i>
                  随时退订，无打扰推送
                </li>
              </ul>
            </div>

            <form
              id="fudiantrace-subscribe"
              data-readdy-form="沸点追踪订阅与线索征集"
              onSubmit={handleSubmit}
              className="rounded-xl border border-background-50/20 bg-background-50 p-5 md:p-6"
            >
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <label className="block">
                  <span className="mb-1 block text-xs font-semibold text-foreground-700">你的称呼</span>
                  <input
                    type="text"
                    name="name"
                    placeholder="例如：阿宝"
                    autoComplete="name"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                  />
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs font-semibold text-foreground-700">邮箱地址</span>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="you@example.com"
                    autoComplete="email"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                  />
                </label>
              </div>

              <label className="mt-3 block">
                <span className="mb-1 block text-xs font-semibold text-foreground-700">你的意向</span>
                <select
                  name="intent"
                  className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 outline-none transition focus:border-primary-400"
                >
                  {INTENTS.map((it) => (
                    <option key={it} value={it}>
                      {it}
                    </option>
                  ))}
                </select>
              </label>

              <label className="mt-3 block">
                <span className="mb-1 block text-xs font-semibold text-foreground-700">内容 / 线索（选填，500 字以内）</span>
                <textarea
                  name="content"
                  rows={3}
                  maxLength={500}
                  placeholder="提交事件线索时，请尽量附上可核实的时间、来源与链接…"
                  className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                ></textarea>
              </label>

              {/* 反垃圾字段 */}
              <div className="form-honey-trap" aria-hidden="true">
                <input
                  type="text"
                  name="contact_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  readOnly
                />
              </div>

              {status === "success" && (
                <p className="mt-3 flex items-start gap-2 rounded-md bg-secondary-100 px-3 py-2.5 text-sm text-secondary-900">
                  <i className="ri-checkbox-circle-line mt-0.5 shrink-0" aria-hidden="true"></i>
                  {message}
                </p>
              )}
              {status === "error" && (
                <p className="mt-3 flex items-start gap-2 rounded-md bg-primary-100 px-3 py-2.5 text-sm text-primary-800">
                  <i className="ri-error-warning-line mt-0.5 shrink-0" aria-hidden="true"></i>
                  {message}
                </p>
              )}

              <button type="submit" className="btn-primary mt-4 w-full">
                提交
                <i className="ri-send-plane-line ml-1.5" aria-hidden="true"></i>
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}